import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

// Inyectado desde App.js cuando ClerkProvider monta. Devuelve el JWT actual o null.
let getAuthToken = async () => null;
export const setAuthTokenGetter = (fn) => {
  getAuthToken = fn;
};

api.interceptors.request.use(async (config) => {
  try {
    const token = await getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (e) {
    // Sin sesión: la petición saldrá sin Authorization y el backend devolverá 401.
  }
  return config;
});

// Empresas
export const getEmpresas = () => api.get("/empresas");
export const getEmpresa = (id) => api.get(`/empresas/${id}`);
export const createEmpresa = (data) => api.post("/empresas", data);
export const updateEmpresa = (id, data) => api.put(`/empresas/${id}`, data);
export const deleteEmpresa = (id) => api.delete(`/empresas/${id}`);
// Duplica la empresa A LA CUENTA DEL USUARIO ACTUAL (copia profunda, privada,
// sin NIF ni credenciales). Vale para propias y para compartidas con lectura.
export const duplicarEmpresa = (id, nombre = null) =>
  api.post(`/empresas/${id}/duplicar`, nombre ? { nombre } : {});

// Ejercicios
export const getEjercicios = (empresaId) => api.get(`/empresas/${empresaId}/ejercicios`);
export const getEjercicio = (id) => api.get(`/ejercicios/${id}`);
export const createEjercicio = (empresaId, data) => api.post(`/empresas/${empresaId}/ejercicios`, data);
export const deleteEjercicio = (id) => api.delete(`/ejercicios/${id}`);

// Importación
// Paso 1 del asistente: analiza el fichero (detecta columnas/encabezado/modo de
// importe) y devuelve una previsualización SIN guardar. Si se pasa `mapping`,
// recalcula la muestra/integridad con la elección del usuario.
export const analizarSumasSaldos = (ejercicioId, file, mapping = null) => {
  const formData = new FormData();
  formData.append("file", file);
  if (mapping) formData.append("mapping", JSON.stringify(mapping));
  return api.post(`/ejercicios/${ejercicioId}/importar/analizar`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Paso final: importa de verdad. `mapping` (opcional) lleva la elección del
// usuario; sin mapping, el backend autodetecta (retrocompatible).
export const importarSumasSaldos = (ejercicioId, file, mapping = null) => {
  const formData = new FormData();
  formData.append("file", file);
  if (mapping) formData.append("mapping", JSON.stringify(mapping));
  return api.post(`/ejercicios/${ejercicioId}/importar`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Cuentas
export const getCuentas = (ejercicioId, estado) => {
  const params = estado ? { estado } : {};
  return api.get(`/ejercicios/${ejercicioId}/cuentas`, { params });
};
export const getPendientes = (ejercicioId) => api.get(`/ejercicios/${ejercicioId}/pendientes`);
export const getErrores = (ejercicioId) => api.get(`/ejercicios/${ejercicioId}/errores`);
export const asignarCuenta = (ejercicioId, data) => api.post(`/ejercicios/${ejercicioId}/asignar`, data);
export const asignarMasivo = (ejercicioId, data) => api.post(`/ejercicios/${ejercicioId}/asignar-masivo`, data);
export const aplicarReglaGlobal = (ejercicioId, data) => api.post(`/ejercicios/${ejercicioId}/aplicar-regla-global`, data);
// Búsqueda de cuentas del ejercicio (cualquier estado) para reclasificar
export const buscarCuentas = (ejercicioId, texto, limit = 50) =>
  api.get(`/ejercicios/${ejercicioId}/cuentas`, { params: { buscar: texto, limit } });
// F1: alta manual de una cuenta (saldo 0). data = {codigo_full, nombre, tipo_destino?, partida_pgc?}
export const crearCuenta = (ejercicioId, data) => api.post(`/ejercicios/${ejercicioId}/cuentas`, data);
// F1: borrar una cuenta creada a mano (las importadas / de asiento no se borran aquí)
export const borrarCuenta = (ejercicioId, cuentaId) =>
  api.delete(`/ejercicios/${ejercicioId}/cuentas/${cuentaId}`);
// W19.4 + sept-2026: renombrar una cuenta. Manual → solo su ejercicio;
// importada → se propaga a toda la empresa y sobrevive a reimportaciones.
export const renombrarCuenta = (ejercicioId, cuentaId, nombre) =>
  api.put(`/ejercicios/${ejercicioId}/cuentas/${cuentaId}`, { nombre });
// A3 (sept-2026): renombrar POR CÓDIGO en toda la empresa (desde Drivers).
export const renombrarCuentaEmpresa = (empresaId, codigoFull, nombre) =>
  api.put(`/empresas/${empresaId}/cuentas/renombrar`, { codigo_full: codigoFull, nombre });

// Margen Bruto configurable (sept-2026): cuentas 60/61/70/71 seleccionables
// a máximo dígito por empresa (la config guarda las EXCLUSIONES).
export const getMargenBruto = (ejercicioId) =>
  api.get(`/ejercicios/${ejercicioId}/margen-bruto`);
export const getMargenConfig = (empresaId) =>
  api.get(`/empresas/${empresaId}/margen-config`);
export const saveMargenConfig = (empresaId, excluidas) =>
  api.put(`/empresas/${empresaId}/margen-config`, { excluidas });
// Inicio personalizable (nov-2026): widgets del panel por empresa
export const getDashboardWidgets = (empresaId) =>
  api.get(`/empresas/${empresaId}/dashboard-widgets`);
export const saveDashboardWidgets = (empresaId, widgets) =>
  api.put(`/empresas/${empresaId}/dashboard-widgets`, { widgets });
export const resetDashboardWidgets = (empresaId) =>
  api.delete(`/empresas/${empresaId}/dashboard-widgets`);
// C1 (sept-2026): margen bruto histórico + proyectado (mismo motor de drivers)
export const getMargenBrutoProyectado = (empresaId) =>
  api.get(`/empresas/${empresaId}/margen-bruto-proyectado`);

// F6: asientos/ajustes manuales de balance (partida doble).
export const getAsientos = (ejercicioId) => api.get(`/ejercicios/${ejercicioId}/asientos`);
export const crearAsiento = (ejercicioId, data) => api.post(`/ejercicios/${ejercicioId}/asientos`, data);
// W19.7: editar un asiento (revierte los apuntes viejos y aplica los nuevos, mismo id)
export const actualizarAsiento = (ejercicioId, asientoId, data) =>
  api.put(`/ejercicios/${ejercicioId}/asientos/${asientoId}`, data);
export const borrarAsiento = (ejercicioId, asientoId) =>
  api.delete(`/ejercicios/${ejercicioId}/asientos/${asientoId}`);

// Partidas
export const getPartidasBalance = () => api.get("/partidas/balance");
export const getPartidasPyg = () => api.get("/partidas/pyg");

// Informes
export const getBalance = (ejercicioId) => api.get(`/ejercicios/${ejercicioId}/balance`);
export const getPyg = (ejercicioId) => api.get(`/ejercicios/${ejercicioId}/pyg`);
export const getDashboard = (ejercicioId, ebitdaModo) =>
  api.get(`/ejercicios/${ejercicioId}/dashboard`, { params: ebitdaModo ? { ebitda_modo: ebitdaModo } : {} });
export const getComparativa = (empresaId, anios, ebitdaModo) =>
  api.get(`/empresas/${empresaId}/comparativa`, { params: { anios, ...(ebitdaModo ? { ebitda_modo: ebitdaModo } : {}) } });

// Checks de validación
export const getChecksEmpresa = (empresaId) => api.get(`/empresas/${empresaId}/checks`);

// Salud de la empresa (semáforo global + áreas con items accionables)
export const getSaludEmpresa = (empresaId) => api.get(`/empresas/${empresaId}/salud`);

// Finoview — importar empresa desde Finoview en vez de Excel
// Lista las empresas disponibles en la cuenta Finoview de la API key dada.
// La key va por CABECERA (X-API-Key), nunca en la URL: los query params quedan
// escritos en los access logs del proxy y del servidor.
export const getFinoviewCompanies = (apiKey) =>
  api.get(`/finoview/companies`, { headers: { "X-API-Key": apiKey } });
// Importa una empresa de Finoview en la empresa indicada. payload: {api_key, finoview_company_id, year}
export const importarFinoview = (empresaId, payload) =>
  api.post(`/empresas/${empresaId}/importar-finoview`, payload);
// Exporta la empresa de FinReport a Finoview. payload: {api_key, years?}
export const exportarFinoview = (empresaId, payload) =>
  api.post(`/empresas/${empresaId}/exportar-finoview`, payload);

// Holded — conexión por empresa: la API key se guarda CIFRADA en el backend y
// el diario se convierte en Sumas y Saldos (los apuntes quedan para drilldown).
export const conectarHolded = (empresaId, apiKey, anios) =>
  api.post(`/empresas/${empresaId}/holded/conectar`, { api_key: apiKey, ...(anios ? { anios } : {}) });
export const getHoldedEstado = (empresaId) => api.get(`/empresas/${empresaId}/holded`);
export const desconectarHolded = (empresaId) => api.delete(`/empresas/${empresaId}/holded`);
export const sincronizarHolded = (empresaId, anios) =>
  api.post(`/empresas/${empresaId}/holded/sincronizar`, anios ? { anios } : {});
export const getHoldedApuntes = (ejercicioId, cuenta, limit = 200) =>
  api.get(`/ejercicios/${ejercicioId}/holded/apuntes`, {
    params: { ...(cuenta ? { cuenta } : {}), limit },
  });
// Import por Libro diario de Holded en EXCEL (sin API key): misma tubería que
// la sincronización (cierre excluido, S&S reconstruido, apuntes para el libro).
export const analizarDiarioHolded = (ejercicioId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/ejercicios/${ejercicioId}/importar-diario-holded/analizar`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const importarDiarioHolded = (ejercicioId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/ejercicios/${ejercicioId}/importar-diario-holded`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
// Plan de proyección pegable: hipótesis como JSON validado (preview → aplicar
// con snapshot de deshacer). Formato: docs/project-control/PLAN_PROYECCION.md.
export const previsualizarPlanProyeccion = (empresaId, plan) =>
  api.post(`/empresas/${empresaId}/proyeccion-plan/previsualizar`, { plan });
export const aplicarPlanProyeccion = (empresaId, plan, solo = null) =>
  api.post(`/empresas/${empresaId}/proyeccion-plan/aplicar`, { plan, ...(solo ? { solo } : {}) });
export const getPlanProyeccionActual = (empresaId) =>
  api.get(`/empresas/${empresaId}/proyeccion-plan/actual`);
export const getPlanSnapshots = (empresaId) =>
  api.get(`/empresas/${empresaId}/proyeccion-plan/snapshots`);
export const restaurarPlanSnapshot = (empresaId, snapshotId) =>
  api.post(`/empresas/${empresaId}/proyeccion-plan/restaurar`, { snapshot_id: snapshotId });
// Libro contable navegable (ejercicios con origen HOLDED o HOLDED_EXCEL):
// balance de sumas y saldos → mayor de una cuenta → asiento línea a línea.
export const getHoldedSumasSaldos = (ejercicioId) =>
  api.get(`/ejercicios/${ejercicioId}/holded/sumas-saldos`);
export const getHoldedMayor = (ejercicioId, cuenta, limit = 1000) =>
  api.get(`/ejercicios/${ejercicioId}/holded/mayor`, { params: { cuenta, limit } });
export const getHoldedAsientos = (ejercicioId, asiento, limit = 500) =>
  api.get(`/ejercicios/${ejercicioId}/holded/asientos`, {
    params: { ...(asiento != null ? { asiento } : {}), limit },
  });

// Exportar
export const exportarEjercicio = (ejercicioId) => {
  return api.get(`/ejercicios/${ejercicioId}/exportar`, {
    responseType: "blob",
  });
};

// Exportar Balance
export const exportarBalance = (ejercicioId) => {
  return api.get(`/ejercicios/${ejercicioId}/exportar-balance`, { responseType: "blob" });
};

// Exportar en formato plantilla (para importación a otra aplicación)
export const exportarPlantilla = (empresaId, anios) => {
  return api.get(`/empresas/${empresaId}/exportar-plantilla?anios=${anios}`, {
    responseType: "blob",
  });
};

// Exportar comparativa multi-año
export const exportarComparativa = (empresaId, anios) => {
  return api.get(`/empresas/${empresaId}/exportar-comparativa?anios=${anios}`, {
    responseType: "blob",
  });
};

// Exportar detallado a 3 dígitos
export const exportarDetallado = (empresaId, anios) => {
  return api.get(`/empresas/${empresaId}/exportar-detallado?anios=${anios}`, {
    responseType: "blob",
  });
};

// Exportar con máximo detalle (código de cuenta completo)
export const exportarDetalladoMax = (empresaId, anios) => {
  return api.get(`/empresas/${empresaId}/exportar-detallado-max?anios=${anios}`, {
    responseType: "blob",
  });
};

// Exportar PyG en formato Modelo Despacho
export const exportarPygDespacho = (ejercicioId) => {
  return api.get(`/ejercicios/${ejercicioId}/exportar-pyg-despacho`, {
    responseType: "blob",
  });
};

// Overrides
export const getOverrides = (empresaId) => {
  const params = empresaId ? { empresa_id: empresaId } : {};
  return api.get("/overrides", { params });
};
export const deleteOverride = (id) => api.delete(`/overrides/${id}`);
export const updateOverride = (id, data) => api.put(`/overrides/${id}`, data);

// Archivos Sumas y Saldos
export const getArchivoSumasSaldos = (ejercicioId) => api.get(`/ejercicios/${ejercicioId}/archivo-sumas-saldos`);
export const descargarSumasSaldos = (ejercicioId) => api.get(`/ejercicios/${ejercicioId}/descargar-sumas-saldos`, { responseType: "blob" });

// Flujo de Caja
export const getFlujoCaja = (ejercicioId) => api.get(`/ejercicios/${ejercicioId}/flujo-caja`);
export const getFlujoCajaComparativa = (empresaId, anios) => api.get(`/empresas/${empresaId}/flujos-caja`, { params: { anios } });

// EBITDA
// EBITDA — modelo nuevo (modo base|ajustado|multianio + ajustes total/parcial/%)
export const getEbitda = (ejercicioId, modo = "ajustado") =>
  api.get(`/ejercicios/${ejercicioId}/ebitda`, { params: { modo } });
export const putEbitdaAjustes = (ejercicioId, ajustes) =>
  api.put(`/ejercicios/${ejercicioId}/ebitda/ajustes`, { ajustes });
// Legacy (compatibilidad)
export const getEbitdaData = (ejercicioId) => api.get(`/ejercicios/${ejercicioId}/ebitda-data`);
export const saveEbitdaConfig = (empresaId, cuentasSeleccionadas) =>
  api.post(`/empresas/${empresaId}/ebitda-config`, { cuentas_seleccionadas: cuentasSeleccionadas });
export const exportarEbitda = (ejercicioId) => {
  return api.get(`/ejercicios/${ejercicioId}/exportar-ebitda`, { responseType: "blob" });
};

export const exportarFlujoCaja = (ejercicioId) => {
  return api.get(`/ejercicios/${ejercicioId}/exportar-flujo-caja`, { responseType: "blob" });
};

export const exportarFlujoCajaDetallado = (ejercicioId) => {
  return api.get(`/ejercicios/${ejercicioId}/exportar-flujo-caja-detallado`, { responseType: "blob" });
};

// Exporta a Excel las variaciones por subcuenta del flujo de caja (el desglose
// del drill-down: código · nombre · saldo t-1 · saldo t · Δ).
export const exportarFlujoVariaciones = (ejercicioId) => {
  return api.get(`/ejercicios/${ejercicioId}/exportar-flujo-variaciones`, { responseType: "blob" });
};

export const exportarComparativaFlujos = (empresaId, anios) => {
  return api.get(`/empresas/${empresaId}/exportar-comparativa-flujos?anios=${anios}`, { responseType: "blob" });
};

export const exportarComparativaFlujosDetallado = (empresaId, anios) => {
  return api.get(`/empresas/${empresaId}/exportar-comparativa-flujos-detallado?anios=${anios}`, { responseType: "blob" });
};

// Proyección - Config global
export const getProyeccionConfig = (empresaId) => {
  return api.get(`/empresas/${empresaId}/proyeccion/config`);
};

export const saveProyeccionConfig = (empresaId, config) => {
  return api.put(`/empresas/${empresaId}/proyeccion/config`, config);
};

// Proyección - Drivers
// UX drivers (sept-2026): todos los drivers guardados (todos los niveles),
// para detectar solapes antes de guardar y pintar el contexto en el editor.
export const getProyeccionDrivers = (empresaId) =>
  api.get(`/empresas/${empresaId}/proyeccion/drivers`);

export const getProyeccionLineas = (empresaId, seccion, nivel) => {
  return api.get(`/empresas/${empresaId}/proyeccion/lineas?seccion=${seccion}&nivel=${nivel}`);
};

// Guarda el driver de una línea. `driver` = {tipo, params} o null (borra el driver).
export const saveProyeccionLinea = (empresaId, lineaId, driver) => {
  return api.put(`/empresas/${empresaId}/proyeccion/lineas/${lineaId}`, { driver });
};

// Defecto de proyección por usuario (para todas sus empresas).
// GET → {modo:'defecto'|'plano'|'ipc', ipc_pct?}. PUT data = {modo, ipc_pct?}.
export const getProyeccionDefault = () => api.get(`/me/proyeccion-default`);
export const saveProyeccionDefault = (data) => api.put(`/me/proyeccion-default`, data);

// F2A: aplica el MISMO driver (o null para borrar) a varias líneas de golpe.
// `lineaIds` = array de ids "{seccion}_{nivel}_{codigo}". Devuelve {aplicadas, errores}.
export const saveProyeccionLineasMasivo = (empresaId, lineaIds, driver) => {
  return api.put(`/empresas/${empresaId}/proyeccion/lineas-masivo`, {
    linea_ids: lineaIds,
    driver,
  });
};

// Proyección - Resultado consolidado (KPIs, PyG proyectado, secciones)
// `escenario` opcional: "base" | "optimista" | "pesimista" (sin él, el backend devuelve base)
export const getProyeccionResultado = (empresaId, escenario) => {
  return api.get(`/empresas/${empresaId}/proyeccion/resultado`, {
    params: escenario ? { escenario } : {},
  });
};
// DFN (PLAN_DFN.md): deuda financiera neta histórico + proyectado, conciliación
// con el pool declarado y composición por cuenta. La config (asimiladas, IFCP,
// umbral) se guarda con saveProyeccionConfig({ dfn: {...} }).
export const getDfn = (empresaId) => api.get(`/empresas/${empresaId}/dfn`);
// Cuentas de balance por bloque NOF (clientes/existencias/proveedores) con saldo
// por año — para marcar cuáles excluir del ciclo operativo.
export const getNofCuentas = (empresaId) =>
  api.get(`/empresas/${empresaId}/proyeccion/nof-cuentas`);

// Proyección - Comparativa de escenarios (base / optimista / pesimista)
export const getProyeccionEscenarios = (empresaId) => {
  return api.get(`/empresas/${empresaId}/proyeccion/escenarios`);
};

// Proyección - Exportación Excel del modelo completo (resumen, PyG, balance,
// flujo, drivers, financiación y escenarios). `escenario` opcional.
export const exportarProyeccion = (empresaId, escenario) => {
  return api.get(`/empresas/${empresaId}/proyeccion/exportar-excel`, {
    params: escenario ? { escenario } : {},
    responseType: "blob",
  });
};

// Exportación Excel del mapa de hipótesis: todos los drivers aplicados (con
// hipótesis, estado y fecha), operaciones y parámetros del modelo (30-09).
export const exportarDriversEmpresa = (empresaId) => {
  return api.get(`/empresas/${empresaId}/exportar-drivers`, { responseType: "blob" });
};

// Proyección - Operaciones (préstamos de financiación e inversiones CAPEX)
// Operación: {id, tipo: "prestamo"|"inversion", nombre, params, cuadro}
export const getProyeccionOperaciones = (empresaId) => {
  return api.get(`/empresas/${empresaId}/proyeccion/operaciones`);
};

export const crearProyeccionOperacion = (empresaId, operacion) => {
  return api.post(`/empresas/${empresaId}/proyeccion/operaciones`, operacion);
};

export const actualizarProyeccionOperacion = (empresaId, opId, operacion) => {
  return api.put(`/empresas/${empresaId}/proyeccion/operaciones/${opId}`, operacion);
};

export const eliminarProyeccionOperacion = (empresaId, opId) => {
  return api.delete(`/empresas/${empresaId}/proyeccion/operaciones/${opId}`);
};

// Notificaciones in-app
export const listarNotificaciones = (params = {}) =>
  api.get("/notificaciones", { params });
export const obtenerUnreadCount = () =>
  api.get("/notificaciones/unread-count");
export const marcarNotificacionLeida = (id) =>
  api.post(`/notificaciones/${id}/read`);
export const marcarTodasLeidas = () =>
  api.post("/notificaciones/read-all");
export const eliminarNotificacion = (id) =>
  api.delete(`/notificaciones/${id}`);

// Compartir empresas
export const listarShares = (empresaId) =>
  api.get(`/empresas/${empresaId}/shares`);
export const crearShare = (empresaId, data) =>
  api.post(`/empresas/${empresaId}/shares`, data);
export const actualizarShare = (empresaId, shareId, data) =>
  api.patch(`/empresas/${empresaId}/shares/${shareId}`, data);
export const revocarShare = (empresaId, shareId) =>
  api.delete(`/empresas/${empresaId}/shares/${shareId}`);
export const listarInvitacionesPendientes = () =>
  api.get("/shares/pending");
export const aceptarInvitacion = (shareId) =>
  api.post(`/shares/${shareId}/accept`);
export const rechazarInvitacion = (shareId) =>
  api.post(`/shares/${shareId}/reject`);

// Diagnóstico / health del motor de cálculo
export const getEbitdaHealth = () => api.get("/diagnostics/ebitda");
export const getSystemHealth = () => api.get("/diagnostics/health");

export default api;
